import { FC } from "react";
import { Button, TextField } from "@mui/material";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ValidationError from "@components/ValidationError/ValidationError";
import { setEmail } from "@store/resetPasswordSlice";
import { AuthService } from "@service/auth.service";
import { useDispatch } from "react-redux";
import { setMessage } from "@store/snackbarSlice";
import CustomLabelInput from "@components/CustomLabelInput";
import PublicForm from "@components/PublicForm";

const authService = AuthService.instance;

const EmailForm: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
  });

  const sendCode = async ({ email }: FieldValues): Promise<void> => {
    try {
      await authService.sendResetPasswordCode(email);
      dispatch(setEmail(email));
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  return (
    <PublicForm.PublicFormContainer
      onSubmit={(e) => {
        clearErrors();
        return handleSubmit(sendCode)(e);
      }}
    >
      <CustomLabelInput label={t("resetPassword.emailInput.label")} id="email">
        <TextField
          {...register("email", {
            required: true,
          })}
          id="email"
          error={!!errors?.email}
          type="email"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("resetPassword.emailInput.placeholder")}
        />
      </CustomLabelInput>

      {errors.root && (
        <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
      )}

      <PublicForm.PublicFormActionContainer>
        <Button
          type="submit"
          disabled={isSubmitting}
          variant="contained"
          size="large"
          fullWidth
        >
          {t("resetPassword.sendCodeButton")}
        </Button>
      </PublicForm.PublicFormActionContainer>
    </PublicForm.PublicFormContainer>
  );
};

export default EmailForm;
